import { getPersistedExperimentTreatment } from '@ms/yammer-data/dist/domains/experiments/selectors';
import { reportInfo } from '@ms/yammer-telemetry';
import type { Authenticator } from '@ms/yammer-web-authenticators';
import {
  AuthenticatorType,
  setAuthenticator as setApiAuthenticator,
  setAuthenticator,
} from '@ms/yammer-web-authenticators';
import { TeamsAuthenticatorV2 } from '@ms/yammer-web-authenticators/dist/teams';
import { getNetworkContext } from '@ms/yammer-web-support/dist/location';
import { isInMetaOsHub, isNAAChannelRecommendedWithTelemetry } from '@ms/yammer-web-support/dist/metaOsHub';
import config from 'bootstrap/config';
import { getTeamsContext } from 'state/teamsContext/selectors';

import { persistConfig } from '../../persistedState';

import { getAuthTokenCache, initializeAuthTokenCache } from './authTokenCache';
import { reportAuthError } from './reportAuthError';

type GetAuthenticator = () => Promise<Authenticator>;
const getAuthenticator: GetAuthenticator = async () => {
  if (await isInMetaOsHub()) {
    initializeAuthTokenCache();

    const isNAASupported = await isNAAChannelRecommendedWithTelemetry();
    if (isNAASupported) {
      reportAuthenticatorType(AuthenticatorType.NestedAppAuth);

      const context = getTeamsContext();
      const tenant = config.adal.tenant;

      const isNewNaaAppIdsOnboardingEnabled = (await getPersistedExperimentTreatment(
        persistConfig.key,
        'NaaNewAppIdsOnboarding'
      )) as boolean;

      if (isNewNaaAppIdsOnboardingEnabled) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (config as any).msal.clientId = 'f4060917-6abe-40d7-baa6-f634c0eda4ac';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (config as any).msal.resources.yammer = 'https://engage.cloud.microsoft/teams-branded-exp/access_as_user';
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (config as any).adal = config.msal;

      const authenticatorOptions = {
        getAuthTokenCache,
        instance: config.adal.instance,
        clientId: config.adal.clientId,
        tenant,
        onError: reportAuthError,
        accountFilter: {
          homeAccountId: context?.userId,
          tenantId: context?.tenantId,
          loginHint: context?.loginHint,
        },
      };

      return new TeamsAuthenticatorV2(authenticatorOptions);
    }

    const teamsAuthenticatorPromise = import(
      /* webpackChunkName: "4-auth-teams" */ '@ms/yammer-web-authenticators/dist/teams'
    );
    reportAuthenticatorType(AuthenticatorType.Teams);

    const { TeamsAuthenticator } = await teamsAuthenticatorPromise;

    return new TeamsAuthenticator(getAuthTokenCache);
  }

  const networkContext = getNetworkContext(window.location.pathname);
  const tenant = networkContext?.networkType === 'Canonical' ? networkContext.permalink : config.msal.tenant;

  // TODO: telemetry needs this configuration which causes the order of the function calls to be important. Refactor this so telemetry loads what it needs from the configuration
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (config as any).adal = config.msal;

  const { default: MsalRedirectAuthenticator } = await import(
    /* webpackChunkName: "4-auth-msal" */ '@ms/yammer-web-authenticators/dist/redirect/MsalRedirectAuthenticator'
  );
  reportAuthenticatorType(AuthenticatorType.MsalRedirect);

  return new MsalRedirectAuthenticator({
    ...config.msal,
    tenant,
  });
};

type SetupAuthenticator = () => Promise<Authenticator>;
export const setupAuthenticator: SetupAuthenticator = async () => {
  const authenticator = await getAuthenticator();
  setAuthenticator(authenticator);
  setApiAuthenticator(authenticator);

  return authenticator;
};

export const reportAuthenticatorType = (authenticatorType: AuthenticatorType) => {
  reportInfo({
    eventName: 'teams_authenticator_info',
    eventProperties: {
      authenticatorType,
    },
  });
};
